import {
  PieChart,
  Pie,
  Cell,
  Label,
  Tooltip,
  ResponsiveContainer
} from "recharts";

export default function CostChart({ pricing }) {
  /* ================= SAFETY ================= */

  if (!pricing) return null;

  const safe = (v) => Number(v) || 0;
  const formatCurrency = (value) => `R ${Number(value).toLocaleString()}`;

  const data = [
    { name: "Fuel", value: safe(pricing.fuelCost) },
    { name: "Transport", value: safe(pricing.transportCost) },
    { name: "Toll", value: safe(pricing.tollCost) },
    { name: "Escort", value: safe(pricing.escortCost) },
    ...(Array.isArray(pricing.additionalCharges)
      ? pricing.additionalCharges.map((item) => ({
        name: item?.label || "Additional Charge",
        value: safe(item?.value)
      }))
      : [])
  ]
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  if (data.length === 0) {
    return <p className="empty">No chart data available</p>;
  }

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#6366f1", "#0f766e", "#a16207"];
  const totalCost = data.reduce((sum, item) => sum + item.value, 0);
  const dataWithMeta = data.map((item) => ({
    ...item,
    percentage: totalCost > 0 ? (item.value / totalCost) * 100 : 0
  }));

  /* ================= RENDER ================= */

  return (
    <div
      className="chart-container"
      role="img"
      aria-label="Cost breakdown chart showing full pricing portions"
    >

      <ResponsiveContainer width="100%" height={260} minWidth={280} minHeight={260}>
        <PieChart>

          <Pie
            data={dataWithMeta}
            dataKey="value"
            nameKey="name"
            outerRadius={90}
            innerRadius={52}
            minAngle={6}
            paddingAngle={2}
          >
            <Label
              position="center"
              content={({ viewBox }) => {
                const { cx, cy } = viewBox || {};
                if (typeof cx !== "number" || typeof cy !== "number") return null;

                return (
                  <g>
                    <text x={cx} y={cy - 8} textAnchor="middle" fill="#64748b" fontSize="11">
                      Total Cost
                    </text>
                    <text x={cx} y={cy + 12} textAnchor="middle" fill="#0f172a" fontSize="14" fontWeight="700">
                      {formatCurrency(totalCost)}
                    </text>
                  </g>
                );
              }}
            />
            {dataWithMeta.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>

          <Tooltip
            formatter={(value, _name, context) => {
              const point = context?.payload;
              const percentage = point?.percentage ?? 0;
              return [`${formatCurrency(value)} (${percentage.toFixed(1)}%)`, "Amount"];
            }}
          />

        </PieChart>
      </ResponsiveContainer>

      <div className="summary-chart" aria-hidden="true">
        {dataWithMeta.map((item, index) => (
          <div key={item.name} className="summary-row">
            <span>
              <span
                className="chart-dot"
                style={{ color: COLORS[index % COLORS.length] }}
                aria-hidden="true"
              >
                ●
              </span>
              {item.name}
            </span>
            <strong>
              {formatCurrency(item.value)} ({item.percentage.toFixed(1)}%)
            </strong>
          </div>
        ))}
      </div>

    </div>
  );
}